import Api from "../../../../../../../../core/services/api/api";
import {PreventionInvalidityCode} from "../preventative-interactions";
import {BaseImportTransformer} from "./shared";
import {normalizeSpreadsheetCellValue, toImportCodeUpper} from "./shared/normalize-spreadsheet-cell-value";

/**
 * Customer / territory / account manager import — phased validation (see {@link BaseImportTransformer} run contract).
 * 1) CSV: missing customer number
 * 2) Batched customer id lookup by code
 * 3) Unknown customer (code present, id missing)
 * 4) Global territory list + salesperson list, then per-row territory / account manager / required-pair rules
 *
 * Preview (`!isFinalRun`): `{ dataList: [] }` placeholders for bad rows + merged `preventativeIssues`.
 * Final (`isFinalRun`): `null` for bad rows; otherwise `{ dataList: [{ customerNo, territoryCode, accountManagerId }] }`.
 */
export class CustomersTerritoriesAndAccountManagersImportTransformer extends BaseImportTransformer {
    /**
     * @returns {Promise<*[]>}
     */
    async #listAllTerritories() {
        const response = await Api.listTerritories(
            {paginationInformation: {currentPage: 1, pageSize: 100}, query: ""},
            undefined,
            false
        );
        if (!response?.resultFlag) {
            return [];
        }
        const items = response.data?.items ?? [];
        if ((response?.data?.total ?? 0) > 100) {
            const additionalPages = Math.ceil((response.data.total - 100) / 100);
            const additionalResponses = await Promise.all(
                Array.from({length: additionalPages}, (_, i) =>
                    Api.listTerritories(
                        {paginationInformation: {currentPage: i + 2, pageSize: 100}, query: ""},
                        undefined,
                        false
                    ))
            );
            for (const res of additionalResponses) {
                if (res?.resultFlag) {
                    items.push(...(res.data?.items ?? []));
                }
            }
        }
        return items;
    }

    /**
     * @param {Array<Object|null|undefined>} mappedData
     * @param {boolean} isFinalRun
     */
    async run(mappedData, isFinalRun = false) {
        const rows = mappedData ?? [];
        const preventativeIssues = [];

        /** @type {{index: number, item: *, customerNoUpper: string, territoryCodeUpper: string, accountManagerCodeUpper: string, codes: string[]}[]} */
        const rowState = rows.map((item, index) => ({
            index,
            item,
            customerNoUpper: toImportCodeUpper(item?.customerNo),
            territoryCodeUpper: toImportCodeUpper(item?.territoryCode),
            accountManagerCodeUpper: toImportCodeUpper(item?.accountManagerCode),
            codes: [],
        }));

        // ── Phase 1: preliminary (CSV-only) ─────────────────────────────────────
        for (const rs of rowState) {
            if (!rs.item) continue;
            if (!rs.customerNoUpper) {
                rs.codes.push(PreventionInvalidityCode.INVALID_CUSTOMER);
            }
        }

        // ── Phase 2: batched customer lookup ─────────────────────────────────────
        const uniqueCustomerCodes = [...new Set(rowState.map((r) => r.customerNoUpper).filter(Boolean))].sort();
        /** @type {Record<string, *>} */
        const customerIdByCode = {};
        if (uniqueCustomerCodes.length > 0) {
            const customerResponses = await Promise.all(
                uniqueCustomerCodes.map((code) => Api.getCustomerSimpleInfoByCode(code, false))
            );
            uniqueCustomerCodes.forEach((code, idx) => {
                customerIdByCode[code] = customerResponses[idx]?.data?.id ?? null;
            });
        }

        // ── Phase 3: unknown customer ───────────────────────────────────────────
        for (const rs of rowState) {
            if (!rs.item) continue;
            if (rs.customerNoUpper && !customerIdByCode[rs.customerNoUpper]) {
                rs.codes.push(PreventionInvalidityCode.INVALID_CUSTOMER);
            }
        }

        // ── Phase 4: reference data (territories + salespeople) ─────────────────
        const allTerritories = await this.#listAllTerritories();
        const allSalesPersons = (await Api.listSalesPersons("", undefined, false))?.data ?? [];

        // ── Phase 5: territory / account manager / required pair (per row) ───────
        for (const rs of rowState) {
            if (!rs.item) continue;

            const territoryCode = rs.territoryCodeUpper || undefined;
            const accountManagerCode = rs.accountManagerCodeUpper || undefined;

            const territory = territoryCode ? allTerritories.find((t) => t.code === territoryCode) : undefined;
            const accountManager = accountManagerCode
                ? allSalesPersons.find((sp) => sp.code === accountManagerCode)
                : undefined;

            if (territoryCode && !territory) {
                rs.codes.push(PreventionInvalidityCode.INVALID_TERRITORY);
            }
            if (accountManagerCode && !accountManager) {
                rs.codes.push(PreventionInvalidityCode.INVALID_ACCOUNT_MANAGER);
            }
            if (!territoryCode && !accountManagerCode) {
                rs.codes.push(PreventionInvalidityCode.MISSING_REQUIRED_FIELDS);
            }
        }

        // ── Emit merged issues ───────────────────────────────────────────────────
        for (const rs of rowState) {
            if (!rs.item) {
                preventativeIssues.push({
                    rowIndex: rs.index,
                    originalRow: rows[rs.index],
                    invalidityCodes: [PreventionInvalidityCode.INVALID_CUSTOMER],
                });
                continue;
            }
            if (!rs.codes.length) continue;
            preventativeIssues.push({
                rowIndex: rs.index,
                originalRow: rs.item,
                invalidityCodes: [...new Set(rs.codes)],
            });
        }

        const issueIndexSet = new Set(preventativeIssues.map((p) => p.rowIndex));

        const apiRequests = rows.map((item, index) => {
            if (!item) {
                return isFinalRun ? null : {
                    dataList: [
                        {customerNo: '', territoryCode: undefined, accountManagerCode: undefined,}
                    ]
                };
            }

            const rs = rowState[index];
            const hasIssue = issueIndexSet.has(index);

            if (hasIssue) {
                return isFinalRun ? null : {
                    dataList: [
                        {
                            customerNo: normalizeSpreadsheetCellValue(item.customerNo),
                            territoryCode: normalizeSpreadsheetCellValue(item.territoryCode) || undefined,
                            accountManagerCode: normalizeSpreadsheetCellValue(item.accountManagerCode) || undefined,
                        }
                    ]
                };
            }

            const territoryCode = rs.territoryCodeUpper || undefined;
            const accountManagerCode = rs.accountManagerCodeUpper || undefined;
            const accountManager = accountManagerCode
                ? allSalesPersons.find((sp) => sp.code === accountManagerCode)
                : undefined;

            return {
                dataList: [
                    {
                        customerNo: normalizeSpreadsheetCellValue(item.customerNo),
                        territoryCode: territoryCode || undefined,
                        accountManagerId: accountManager?.operatorId ?? undefined,
                    },
                ],
            };
        });

        return {apiRequests, preventativeIssues};
    }
}
